import isNull from 'lodash/isNull';
import { snapshot } from 'valtio';
import { JiraComponent, JiraEpic, JiraProject, JiraUser } from 'common-types';
import { logger } from 'logging-utils';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import jiraService from '../services';
import {
  setDependentByProjectField,
  setLoadingJiraAssignees,
  setLoadingJiraComponents,
  setLoadingJiraEpics,
  setLoadingJiraProjects,
  setLoadingJiraReporters,
  setProjects,
} from '../store-actions/jira-ticket-store-actions';
import JiraTicketStore from '../store/jira-ticket-store';

export const searchProjects = async (searchString: string) => {
  if (searchString === '') {
    const { jiraTicket } = snapshot(JiraTicketStore);
    setProjects(jiraTicket.project ? [jiraTicket.project] : []);
    return;
  }
  try {
    setProjects([]);
    setLoadingJiraProjects(true);

    const results = await jiraService.searchProjects.execute(searchString);
    if (isNull(results)) {
      throw new Error('Invalid response');
    }
    let newOptions: JiraProject[] = [...results.searchProjects];
    const { jiraTicket } = snapshot(JiraTicketStore);

    if (
      jiraTicket.project &&
      !results.searchProjects.some(p => p.key === jiraTicket.project?.key)
    ) {
      newOptions.push(jiraTicket.project);
    }

    setProjects(newOptions);
  } catch (error) {
    logger.error(error);
    openSnackbar(
      `Failed to load the requested project. Please try again later..`,
      {
        variant: 'warning',
      },
    );
  } finally {
    setLoadingJiraProjects(false);
  }
};

export const searchAssignee = async (searchString: string) => {
  if (searchString === '') {
    const { jiraTicket } = snapshot(JiraTicketStore);
    setDependentByProjectField(
      jiraTicket.assignee ? [jiraTicket.assignee] : [],
      'assigness',
    );
    return;
  }
  try {
    setDependentByProjectField([], 'assigness');
    setLoadingJiraAssignees(true);
    const { jiraTicket } = snapshot(JiraTicketStore);
    const results = await jiraService.searchAssignee.execute(searchString);
    if (isNull(results)) {
      throw new Error('Invalid response');
    }
    let newOptions: JiraUser[] = [...results.searchAssignee];

    if (
      jiraTicket.assignee &&
      !results.searchAssignee.some(
        u => u.emailAddress === jiraTicket.assignee?.emailAddress,
      )
    ) {
      newOptions.push(jiraTicket.assignee);
    }
    setDependentByProjectField(newOptions, 'assigness');
  } catch (error) {
    logger.error(error);
    openSnackbar(
      `Failed to load the requested assignee. Please try again later..`,
      {
        variant: 'warning',
      },
    );
  } finally {
    setLoadingJiraAssignees(false);
  }
};

export const searchReporter = async (searchString: string) => {
  if (searchString === '') {
    const { jiraTicket } = snapshot(JiraTicketStore);
    setDependentByProjectField(
      jiraTicket.reporter ? [jiraTicket.reporter] : [],
      'reporters',
    );
    return;
  }
  try {
    setDependentByProjectField([], 'reporters');
    setLoadingJiraReporters(true);

    const { jiraTicket } = snapshot(JiraTicketStore);
    const results = await jiraService.searchReporter.execute(searchString);
    if (isNull(results)) {
      throw new Error('Invalid response');
    }
    let newOptions: JiraUser[] = [...results.searchReporter];

    if (
      jiraTicket.reporter &&
      !results.searchReporter.some(
        u => u.emailAddress === jiraTicket.reporter?.emailAddress,
      )
    ) {
      newOptions.push(jiraTicket.reporter);
    }
    setDependentByProjectField(newOptions, 'reporters');
  } catch (error) {
    logger.error(error);
    openSnackbar(
      `Failed to load the requested reporter. Please try again later..`,
      {
        variant: 'warning',
      },
    );
  } finally {
    setLoadingJiraReporters(false);
  }
};

export const searchComponents = async (searchString: string) => {
  if (searchString === '') {
    const { dependentByProjectFields } = snapshot(JiraTicketStore);
    dependentByProjectFields?.components &&
      setDependentByProjectField(
        dependentByProjectFields.components.length > 0
          ? [...dependentByProjectFields.components]
          : [],
        'components',
      );
    return;
  }
  try {
    setDependentByProjectField([], 'components');
    setLoadingJiraComponents(true);
    const { jiraTicket } = snapshot(JiraTicketStore);
    if (!jiraTicket.project) return;
    const results = await jiraService.searchComponents.execute({
      searchString,
      projectKey: jiraTicket.project.key,
    });
    if (isNull(results)) {
      throw new Error('Invalid response');
    }
    let newOptions: JiraComponent[] = [...results.getComponentsByProjectKey];

    if (jiraTicket.components?.length && jiraTicket.components.length > 0) {
      jiraTicket.components.forEach(c => {
        if (!newOptions.some(o => o.id === c.id)) {
          newOptions.push(c);
        }
      });
    }
    setDependentByProjectField(newOptions, 'components');
  } catch (error) {
    logger.error(error);
    openSnackbar(
      `Failed to load the requested component. Please try again later..`,
      {
        variant: 'warning',
      },
    );
  } finally {
    setLoadingJiraComponents(false);
  }
};

export const searchEpics = async (searchString: string) => {
  if (searchString === '') {
    const { jiraTicket } = snapshot(JiraTicketStore);
    setDependentByProjectField(
      jiraTicket.epicLink ? [jiraTicket.epicLink] : [],
      'epics',
    );
    return;
  }
  try {
    setLoadingJiraEpics(true);
    const { jiraTicket } = snapshot(JiraTicketStore);
    if (!jiraTicket.project) return;
    const results = await jiraService.searchEpics.execute({
      searchString,
      projectKey: jiraTicket.project.key,
    });
    if (isNull(results)) {
      throw new Error('Invalid response');
    }
    let newOptions: JiraEpic[] = isNull(results.getEpicsByProjectKey)
      ? []
      : [...results.getEpicsByProjectKey];

    if (
      jiraTicket.epicLink &&
      !newOptions.some(o => o.key === jiraTicket.epicLink?.key)
    ) {
      newOptions.push(jiraTicket.epicLink);
    }
    setDependentByProjectField(newOptions, 'epics');
  } catch (error) {
    logger.error(error);
    openSnackbar(
      `Failed to load the requested epic. Please try again later..`,
      {
        variant: 'warning',
      },
    );
  } finally {
    setLoadingJiraEpics(false);
  }
};
